import type { DigitalTwin, DigitalTwinSettingTarget } from 'api/digitalTwin/digitalTwin.api'
import type { TFunction } from 'i18next'
import { getHelpTextPrettyName, getPrettyName } from 'views/DigitalTwinSettingsView/DigitalTwinSettingsView.helper'
import type { SettingFormMode } from './settingFormMode.types'

function getPropertyTranslationKey({ level, name, attribute }: DigitalTwinSettingTarget): string {
  return `${level}.${name}.${attribute}`
}

/** `Object: Property`, in the digital twin's translations. */
export function getPropertyTitle(property: DigitalTwinSettingTarget, digitalTwin: DigitalTwin): string {
  const objectName = getPrettyName(`${property.level}.${property.name}`, digitalTwin.translations)
  const attributeName = getPrettyName(getPropertyTranslationKey(property), digitalTwin.translations)
  return `${objectName}: ${attributeName}`
}

export function getPropertyHelpText(property: DigitalTwinSettingTarget, digitalTwin: DigitalTwin): string {
  return getHelpTextPrettyName(getPropertyTranslationKey(property), digitalTwin.translations)
}

type EditorTitleParams = {
  /** Null while the user has not chosen the property in the form yet. */
  propertyTitle: string | null
  /** Null when no setting is open, only the property's settings. */
  editedSetting: { isTimeSeries: boolean; mode: SettingFormMode } | null
  t: TFunction<'translation', undefined>
}

export function getEditorTitle({ propertyTitle, editedSetting, t }: EditorTitleParams): string {
  if (propertyTitle === null) {
    return editedSetting?.isTimeSeries ? t('Create new time series setting') : t('Create new setting')
  }

  if (!editedSetting) {
    return propertyTitle
  }

  const { isTimeSeries, mode } = editedSetting
  switch (mode) {
    case 'edit':
      return isTimeSeries
        ? t('Modify time series setting for {{setting}}', { setting: propertyTitle })
        : t('Modify setting for {{setting}}', { setting: propertyTitle })
    case 'copy':
      return isTimeSeries
        ? t('Copy time series setting for {{setting}}', { setting: propertyTitle })
        : t('Copy setting for {{setting}}', { setting: propertyTitle })
    case 'create':
      return isTimeSeries
        ? t('Create time series setting for {{setting}}', { setting: propertyTitle })
        : t('Create setting for {{setting}}', { setting: propertyTitle })
  }
}

export function getSubmitLabel(
  { isTimeSeries, mode }: { isTimeSeries: boolean; mode: SettingFormMode },
  t: TFunction<'translation', undefined>
): string {
  switch (mode) {
    case 'edit':
      return t('Save changes')
    case 'copy':
      return isTimeSeries ? t('Create time series copy') : t('Create copy')
    case 'create':
      return isTimeSeries ? t('Create time series setting') : t('Create setting')
  }
}
